import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    computed,
    input,
    output
} from '@angular/core';
import { AppService } from 'app/app.service';
import { LocalStore, MatchMediaService } from 'app/services';

import { BaseComponent } from 'app/shared/components';

import { MediaInfoResponse } from '@key-telematics/fleet-api-client';
import { FilterPanelService } from 'app/shared/components/filter-panel/filter-panel.service';
import { DateRange, getTimePeriodForDates } from 'app/shared/utils/dates';
import moment from 'moment-timezone';
import { BehaviorSubject, Observable, fromEvent, interval } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, startWith, takeUntil } from 'rxjs/operators';
import { DateHeaderSettings, ValidTimePeriod } from '../../dates/date-range-selection.component';

export interface ListItem extends MediaInfoResponse {
    listFilter?: any;
    [key: string]: any;
}

export const VideoListLocalStore = new LocalStore('video-list-component');

@Component({
    selector: 'key-video-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    providers: [{ provide: LocalStore, useValue: VideoListLocalStore }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class VideoListComponent extends BaseComponent implements OnInit {

    isMobile: boolean;

    videoListing: ListItem[] = [];

    windowResize: Observable<Event> = fromEvent(window, 'resize').pipe(
        takeUntil(this.destroyed),
        startWith(new CustomEvent('resize')),
        debounceTime(100)
    );

    headerActions = input<DateHeaderSettings>();
    displayMessage = input<string>();
    showSaveVideoButton = input<boolean>(false);
    showDeleteVideoButton = input<boolean>(false);
    startDate = input<string>();
    endDate = input<string>();
    listLoaded = input<boolean>();

    @Input() title: string;
    @Input() show: boolean;
    @Input() count: number;
    @Input() selectedId: string;
    @Input() errorMessage: string;
    @Input() page = 1;
    @Input() showDateSelect = true;
    @Input() noVideosMessage: string;
    @Input() columnLayoutForVideoListitem = true;

    @Input() showFilterByValue = true;
    loadingMore = input<boolean>(false);
    scrollPagination = input<boolean>(false);

    private readonly defaultDatePeriod: ValidTimePeriod = 'today';
    private footerInView = new BehaviorSubject<boolean>(true);

    defaultMaxDate = moment().startOf('day').toISOString();
    datePeriod = computed<ValidTimePeriod | null>(() => {
        const start = this.startDate();
        const end = this.endDate();
        if (!start || !end) { return this.defaultDatePeriod; }
        return getTimePeriodForDates(new Date(start), new Date(end)) as ValidTimePeriod | null;
    });

    limit = 20;

    get pageCount(): number {
        return (this.count / this.limit);
    }

    @Input() set videos(videos: MediaInfoResponse[]) {
        this.videoListing = videos.map(x => this.createListItem(x));
    }

    @Output() itemSelected = new EventEmitter<MediaInfoResponse>();
    @Output() pageChanged = new EventEmitter<number>();
    @Output() requestVideo = new EventEmitter();
    saveSelectedVideo = output<MediaInfoResponse>();
    deleteSelectedVideo = output<MediaInfoResponse>();
    loadMore = output();

    constructor(
        public app: AppService,
        private ref: ChangeDetectorRef,
        private matchMedia: MatchMediaService,
        private filterService: FilterPanelService
    ) {
        super();

        this.on(this.matchMedia.isDesktop, desktop => {
            this.isMobile = !desktop;
        });

        this.on(this.filterService.filters$, f => {
            this.page = f.page || 1;
            this.limit = f.limit || this.limit;
            this.ref.markForCheck();
        });

        this.on(this.filterService.total$, total => {
            this.count = total || 0;
            this.ref.markForCheck();
        });

        this.footerInView.asObservable()
        .pipe(
            takeUntil(this.destroyed),
            distinctUntilChanged(),
            filter(inView => this.scrollPagination() && !this.loadingMore() && inView),
        ).subscribe(() => {
            this.loadMore.emit()
        });
    }

    ngOnInit() {
        interval(30e3)
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => {
                // This is so the html can be re-evaluated and we can get the correct date time ago.
                this.ref.markForCheck();
            });
    }

    createListItem(video: MediaInfoResponse): ListItem {
        const result = {
            ...video,
            name: video.asset.name,
            last_update: this.createLastUpdate(video),
            listFilter: video.asset.name,
            filterByValue: this.showFilterByValue,
        };
        return result;
    }

    createLastUpdate(video: MediaInfoResponse) {
        if (video.date) {
            const dateEpoch = this.getDateEpoch(video.date);
            return { // Transform into a KuiRow-compatible format
                display: this.getDisplayDate(video),
                field: dateEpoch,
                value: dateEpoch,
            };
        }
        return { display: '', field: '', value: '' };
    }

    getDisplayDate(video: MediaInfoResponse): string {
        return video.date ? moment.utc(video.date).fromNow() : '';
    }

    reset() {
        this.videoListing = [];
    }

    getDateEpoch(date: string): string {
        return moment(date).format('x');
    }

    onPageChanged(page: number) {
        this.pageChanged.emit(page);
    }

    onDateSelect(date: DateRange) {
        this.filterService.update({
            date
        });
    }

    loadMoreRequest(visible: boolean) {
        this.footerInView.next(visible);
    }
}
